import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'
import _ from 'lodash'
import cx from 'classnames'

import config from '@config'

import hocBlock from '../../../../../../../components/Workspace/components/HocBlock'

import LegendTable from '../../../../../../../../../components/LegendTable'
import CustomDate from '../../../../../../../../../components/CustomDate'
import GdprIcon from '../../../../../../../../../components/GdprIcon'

import Terminology from './components/Terminology'
import ScoringResults from './components/ScoringResults'
import ScoringChart from './components/Chart'
import Degrees from './components/Degrees'

import { LOCALE_PREFIX, NA_CODES } from './constants'

import './styles.scss'

@hocBlock(config.modules.report.key, { levelReload: true })
@injectIntl
export default class AlzaScoring extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        customData: PropTypes.object,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object,
        level: PropTypes.string.isRequired,
        module: PropTypes.string.isRequired,
        data: PropTypes.array,
        saveCustomData: PropTypes.func,
        transformedData: PropTypes.object,
    }

    getInterval = (legendItem, currentCode) => {
        if (!legendItem?.probabilityOfDefault || (currentCode === 'NQ' && legendItem.code === currentCode)) return ''

        const lowerBound = legendItem.probabilityOfDefault?.lowerBound
        const upperBound = legendItem.probabilityOfDefault?.upperBound

        if (typeof lowerBound !== 'number') return ''
        if (typeof upperBound !== 'number') return `${lowerBound} %`

        return `${lowerBound} - ${upperBound} %`
    }

    getSetup = () =>
        _.map(this.props.transformedData?.summary?.legend, (legendItem) => {
            const { intl } = this.props
            const dataCode = NA_CODES.includes(this.props.transformedData?.summary?.data?.code)
                ? 'NQ'
                : this.props.transformedData?.summary?.data?.code
            const isActive = dataCode === legendItem.code

            return {
                ...legendItem,
                active: isActive,
                setup: [
                    { identifier: 'thermometer', header: { minWidth: '12px' } },
                    {
                        identifier: 'label',
                        arrow: true,
                        visibleDefault: !isActive,
                        style: {
                            minWidth: isActive ? '203px' : '180px',
                            color: legendItem.color,
                            fontWeight: isActive ? 900 : 'auto',
                            fontSize: isActive && '22px',
                            paddingLeft: !isActive && '25px',
                            borderRight: '1px solid #4d4d4d',
                        },
                        value: legendItem.degree,
                        header: {
                            label: intl.formatMessage({ id: `${LOCALE_PREFIX}.degreeLabel` }),
                            style: { lineHeight: '40px', minWidth: '180px', paddingLeft: '0px' },
                        },
                    },
                    {
                        identifier: 'label',
                        style: { borderRight: '1px solid #4d4d4d', width: '33.3%' },
                        value: isActive ? (
                            <div className={cx('description-active', dataCode)}>
                                <div>{legendItem.description}</div>
                                {dataCode !== 'NQ' && (
                                    <div>
                                        ({intl.formatMessage({ id: `${LOCALE_PREFIX}.lastChangeText` })}
                                        <CustomDate
                                            date={_.get(
                                                this.props.transformedData?.summary?.data,
                                                'lastChangedDate.value'
                                            )}
                                            precision={_.get(
                                                this.props.transformedData?.summary?.data,
                                                'lastChangedDate.precision'
                                            )}
                                        />
                                        )
                                    </div>
                                )}
                            </div>
                        ) : (
                            legendItem.description
                        ),
                        header: {
                            label: intl.formatMessage({ id: `${LOCALE_PREFIX}.descriptionLabel` }),
                            style: { lineHeight: '40px' },
                        },
                    },
                    {
                        identifier: 'label',
                        style: {
                            flex: '1 0 auto',
                            fontWeight: isActive ? 600 : 'auto',
                            position: 'relative',
                            minWidth: '110px',
                        },
                        value: (
                            <div
                                style={{
                                    position: 'absolute',
                                    right: isActive ? '9%' : '5%',
                                    fontSize: isActive ? '24px' : 'auto',
                                }}
                            >
                                {isActive && dataCode !== 'NQ'
                                    ? `${_.get(this.props.transformedData?.summary?.data, 'probabilityOfDefault')}%`
                                    : this.getInterval(legendItem, dataCode)}
                            </div>
                        ),
                        header: {
                            label: intl.formatMessage({ id: `${LOCALE_PREFIX}.probabilityLabel` }, { br: <br /> }),
                        },
                    },
                ],
            }
        })

    render() {
        const { module, block, id, level, data, customData, saveCustomData, transformedData, intl } = this.props

        const [parsedLevel] = level
            .split(',')
            .map((x) => parseInt(x, 10))
            .slice(-1)

        return (
            <div className={'bisnode-scoring'}>
                <div className={'gdrp-container'}>
                    <GdprIcon id={id} block={block}>
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.gdprTooltip`}
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>,
                                br: <br />,
                                acz: (chunks) => (
                                    <a
                                        href="mailto:podpora.cz@dnb.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#7fcdee', textDecoration: 'underline' }}
                                    >
                                        {chunks}
                                    </a>
                                ),
                                ask: (chunks) => (
                                    <a
                                        href="mailto:podpora.sk@dnb.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#7fcdee', textDecoration: 'underline' }}
                                    >
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    </GdprIcon>
                </div>

                <div className={'bisnode-scoring-head-information-wrap'}>
                    <div className={'bisnode-scoring-refresh-wrap'}>
                        <span>{intl.formatMessage({ id: `${LOCALE_PREFIX}.refreshText` })}</span>
                        <CustomDate
                            date={_.get(transformedData?.summary?.data, 'refreshDate.value')}
                            precision={_.get(transformedData?.summary?.data, 'refreshDate.precision')}
                        />
                    </div>
                    {_.get(transformedData?.summary?.data, 'financialStatementsYear') && (
                        <div className={'bisnode-scoring-year-of-financial-statement-wrap'}>
                            <span>
                                {intl.formatMessage(
                                    { id: `${LOCALE_PREFIX}.yearOfFinancialStatements` },
                                    { year: transformedData?.summary?.data?.financialStatementsYear }
                                )}
                            </span>
                        </div>
                    )}
                </div>
                <div className={'bisnode-scoring-legend-wrap'}>
                    <LegendTable legend={this.getSetup()} />
                    <ScoringResults data={transformedData?.summary?.data} level={level} />
                </div>
                <Terminology customData={customData} saveCustomData={saveCustomData} />
                {parsedLevel >= 20 && !_.isEmpty(transformedData?.scoringDegrees || []) && (
                    <>
                        <div className={'title-container'}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.historyOfScoringLevels`} />
                        </div>
                        <div className={'scoring-degress-wrapper'}>
                            <Degrees
                                module={module}
                                block={block}
                                id={id}
                                legend={transformedData?.summary?.legend}
                                data={transformedData?.scoringDegrees || []}
                            />
                        </div>
                    </>
                )}
                {parsedLevel >= 30 && (
                    <div>
                        <div className={'title-container'}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.evolutionOfProbabilityOfBankruptcy`} />
                        </div>
                        <ScoringChart
                            data={transformedData?.chart?.data || []}
                            originalData={data}
                            module={module}
                            level={level}
                            block={block}
                            id={id}
                            customData={customData}
                        />
                    </div>
                )}
            </div>
        )
    }
}
